import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  RotateCcw,
  Unplug,
} from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"

import {
  DECISION_SOURCE_LABEL,
  DecisionBadge,
  decisionTone,
  formatAbs,
  formatDelta,
  formatMoment,
  MATCH_LABEL,
  matchLabel,
  ReviewView,
  RuleDiffView,
  SeonDecision,
  TONE_TEXT,
} from "@/components/seon/live-parts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { Segmented } from "@/components/ui/segmented"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumeric,
  TableRow,
} from "@/components/ui/table"
import {
  getSeonLivePairs,
  getSeonLivePipe,
  getSeonLiveSummary,
  type LiveDecisionSource,
  type LivePair,
  type LivePairsPage,
  type LivePeriod,
  type LivePeriodQuery,
  type LiveRuleCount,
  type LiveSummary,
  type SeonPipe,
} from "@/lib/api"
import { EMPTY, formatInt, formatPercent, formatScore } from "@/lib/format"
import { cn } from "@/lib/utils"

/**
 * «Живые: мы и SEON» (AMEND-59 шаг 2) — решения двойника рядом с решениями,
 * которые SEON прислал вебхуком по тем же транзакциям.
 *
 * Пара — строка двойника и последняя версия вебхука, которую приёмник склеил
 * с ней (AMEND-59 шаг 3б): по номеру заказа, иначе по IP / email / BIN и
 * времени; старый путь — тот же `transaction_id`. Решение SEON — ручная
 * проверка, иначе `state` ответа, иначе порог балла («по баллу»).
 * Свод, лента и карточка считают одно и то же сравнение (`compare.py`) на
 * сервере; экран только раскладывает ответ. Пустая сторона называется прямо —
 * «SEON ещё не прислал» / «нет у нас», — а не нулём: ноль читался бы как
 * «оценили и сочли чистой».
 */

const PERIOD_OPTIONS: { value: LivePeriod; label: string }[] = [
  { value: "today", label: "Сегодня" },
  { value: "7d", label: "7 дней" },
  { value: "custom", label: "Период" },
]

/** Как `MAX_CUSTOM_DAYS` в `services/admin_api/seon_live.py`. */
const MAX_CUSTOM_DAYS = 31
const PAGE_SIZE = 50
const DAY_MS = 24 * 60 * 60 * 1000
const DECISIONS = ["approve", "review", "decline"] as const

/** День `YYYY-MM-DD` по UTC: окна ручки — сутки UTC, не часы браузера. */
function utcDay(offsetDays = 0) {
  return new Date(Date.now() + offsetDays * DAY_MS).toISOString().slice(0, 10)
}

const UTC_MOMENT = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

function formatUtc(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? EMPTY : UTC_MOMENT.format(date)
}

/** Что не так со своим периодом — `null`, если запрос можно слать. */
function customProblem(since: string, until: string): string | null {
  if (!since || !until) return "Выберите обе даты."
  if (since > until) return "Начало периода позже конца — поменяйте даты местами."
  const days = (Date.parse(until) - Date.parse(since)) / DAY_MS + 1
  if (days > MAX_CUSTOM_DAYS) return `Период — не длиннее ${MAX_CUSTOM_DAYS} суток.`
  return null
}

/** Порог |Δ| из поля ввода: пусто — без порога, мусор — `invalid`. */
function parseMinDelta(text: string): { value: string | undefined; invalid: boolean } {
  const trimmed = text.trim().replace(",", ".")
  if (trimmed === "") return { value: undefined, invalid: false }
  const numeric = Number(trimmed)
  if (Number.isNaN(numeric) || numeric < 0 || numeric > 1000) {
    return { value: undefined, invalid: true }
  }
  return { value: trimmed, invalid: false }
}

function errorText(cause: unknown) {
  return cause instanceof Error ? cause.message : "Request failed"
}

/**
 * Загрузка одной ручки: прерывается при смене параметров, а ответ от старых
 * параметров не перетирает новый (флаг `ignore`, как в `MatrixPanel`).
 */
function useLoad<T>(
  load: ((signal: AbortSignal) => Promise<T>) | null,
  deps: React.DependencyList,
) {
  const [data, setData] = React.useState<T | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    if (load === null) {
      setLoading(false)
      return
    }
    const controller = new AbortController()
    let ignore = false
    setLoading(true)
    load(controller.signal)
      .then((result) => {
        if (ignore) return
        setData(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (ignore || controller.signal.aborted) return
        setData(null)
        setError(errorText(cause))
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })
    return () => {
      ignore = true
      controller.abort()
    }
    // `load` пересоздаётся на каждый рендер; когда слать запрос, решают `deps`.
  }, deps)

  return { data, error, loading }
}

export function LiveTab() {
  const [period, setPeriod] = React.useState<LivePeriod>("today")
  const [since, setSince] = React.useState(() => utcDay(-6))
  const [until, setUntil] = React.useState(() => utcDay())
  const [reload, setReload] = React.useState(0)

  const [mismatch, setMismatch] = React.useState(false)
  const [unpaired, setUnpaired] = React.useState(false)
  const [deltaText, setDeltaText] = React.useState("")
  const [minDelta, setMinDelta] = React.useState<string | undefined>(undefined)
  const [offset, setOffset] = React.useState(0)
  const [open, setOpen] = React.useState<string | null>(null)

  const problem = period === "custom" ? customProblem(since, until) : null
  const query: LivePeriodQuery =
    period === "custom" ? { period, from: since, to: until } : { period }
  const parsedDelta = parseMinDelta(deltaText)

  // Порог применяется после паузы в наборе: иначе «15» ушло бы двумя
  // запросами, и лента мигнула бы выборкой по «1».
  React.useEffect(() => {
    if (parsedDelta.invalid) return
    const timer = window.setTimeout(() => {
      setMinDelta(parsedDelta.value)
      setOffset(0)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [parsedDelta.value, parsedDelta.invalid])

  const pipe = useLoad<SeonPipe>((signal) => getSeonLivePipe(signal), [reload])
  const summary = useLoad<LiveSummary>(
    problem ? null : (signal) => getSeonLiveSummary(query, signal),
    [period, query.from, query.to, problem, reload],
  )
  const pairs = useLoad<LivePairsPage>(
    problem
      ? null
      : (signal) =>
          getSeonLivePairs(
            { ...query, mismatch, unpaired, min_delta: minDelta, limit: PAGE_SIZE, offset },
            signal,
          ),
    [period, query.from, query.to, problem, mismatch, unpaired, minDelta, offset, reload],
  )

  const busy = pipe.loading || summary.loading || pairs.loading

  function changePeriod(value: LivePeriod) {
    setPeriod(value)
    setOffset(0)
    setOpen(null)
  }

  return (
    <div className="space-y-4 p-5">
      {pipe.data?.silent && <SilenceAlarm pipe={pipe.data} />}

      <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="card-title">Живые решения: мы и SEON</h2>
              <button
                type="button"
                onClick={() => setReload((value) => value + 1)}
                aria-label="Обновить"
                className="text-fg-subtle transition-colors duration-150 hover:text-accent"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} strokeWidth={2} />
              </button>
            </div>
            <p className="mt-0.5 max-w-xl text-xs leading-snug text-fg-subtle">
              Решение двойника против решения, которое SEON прислал вебхуком по той же транзакции.
            </p>
          </div>
          <Segmented
            label="Период сравнения"
            options={PERIOD_OPTIONS}
            value={period}
            onChange={changePeriod}
          />
        </header>

        {period === "custom" && (
          <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-sunken px-4 py-2">
            <label className="stencil-label text-fg-muted" htmlFor="live-from">
              С
            </label>
            <Input
              id="live-from"
              type="date"
              value={since}
              onChange={(event) => {
                setSince(event.target.value)
                setOffset(0)
              }}
              className="w-36"
            />
            <label className="stencil-label text-fg-muted" htmlFor="live-to">
              По
            </label>
            <Input
              id="live-to"
              type="date"
              value={until}
              onChange={(event) => {
                setUntil(event.target.value)
                setOffset(0)
              }}
              className="w-36"
            />
            {problem ? (
              <Notice tone="warn">{problem}</Notice>
            ) : (
              <span className="text-xs text-fg-muted">
                Обе даты включительно, сутки по UTC, не длиннее {MAX_CUSTOM_DAYS}.
              </span>
            )}
          </div>
        )}

        <div className="px-4 py-3">
          <PipeStrip pipe={pipe.data} error={pipe.error} loading={pipe.loading} />
        </div>

        <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-line px-4 py-2">
          <span className="stencil-label text-fg-muted">Свод за период</span>
          <span className="text-xs text-fg-subtle">
            {summary.data
              ? `${formatUtc(summary.data.since)} → ${formatUtc(summary.data.until)} UTC`
              : EMPTY}
          </span>
        </div>
        <div className="px-4 pb-4">
          {problem ? (
            <p className="py-6 text-center text-xs text-fg-muted">
              Свод появится, когда период будет задан верно.
            </p>
          ) : summary.error ? (
            <PanelError message={summary.error} onRetry={() => setReload((value) => value + 1)} />
          ) : (
            <SummaryTiles summary={summary.data} loading={summary.loading && !summary.data} />
          )}
        </div>
      </section>

      {!problem && !summary.error && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <MatrixPanel summary={summary.data} loading={summary.loading && !summary.data} />
          <DeltaPanel summary={summary.data} loading={summary.loading && !summary.data} />
          <TopRulesPanel summary={summary.data} loading={summary.loading && !summary.data} />
        </div>
      )}

      <FeedPanel
        page={pairs.data}
        error={pairs.error}
        loading={pairs.loading}
        blocked={problem !== null}
        mismatch={mismatch}
        unpaired={unpaired}
        deltaText={deltaText}
        deltaInvalid={parsedDelta.invalid}
        open={open}
        offset={offset}
        onMismatch={(value) => {
          setMismatch(value)
          setOffset(0)
        }}
        onUnpaired={(value) => {
          setUnpaired(value)
          setOffset(0)
        }}
        onDelta={setDeltaText}
        onReset={() => {
          setMismatch(false)
          setUnpaired(false)
          setDeltaText("")
          setMinDelta(undefined)
          setOffset(0)
        }}
        onToggle={(key) => setOpen((value) => (value === key ? null : key))}
        onOffset={setOffset}
        onRetry={() => setReload((value) => value + 1)}
      />
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* Труба вебхуков                                                            */
/* ------------------------------------------------------------------------- */

/**
 * Сутки тишины при живом двойнике: решения у нас идут, а принятых вебхуков
 * SEON нет. Отдельно и над всем экраном — пустая лента без этого читалась бы
 * как «просто не было пар», а это поломка приёма.
 */
function SilenceAlarm({ pipe }: { pipe: SeonPipe }) {
  const somethingCame = pipe.rejected + pipe.broken > 0
  return (
    <div
      role="alert"
      className="animate-panel-in flex items-start gap-3 rounded border border-decline/40 bg-decline/10 px-4 py-3"
    >
      <Unplug className="mt-0.5 h-4 w-4 shrink-0 text-decline" strokeWidth={2} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-decline">
          Сутки без вебхуков SEON при живых решениях двойника
        </p>
        <p className="mt-1 text-xs leading-relaxed text-fg-muted">
          Решений двойника за последние 24 часа: {formatInt(pipe.twin_last_24h)}, а принятых
          вебхуков SEON не было
          {pipe.last_accepted_at
            ? ` — последний пришёл ${formatMoment(pipe.last_accepted_at)}.`
            : " ни одного за всё время."}{" "}
          {somethingCame
            ? "Сегодня запросы доходили, но отклонены по подписи или не разобраны — смотрите счётчики ниже."
            : "Сегодня не приходило вообще ничего: проверьте, что вебхук зарегистрирован в панели SEON и адрес верный."}
        </p>
      </div>
    </div>
  )
}

function PipeStrip({
  pipe,
  error,
  loading,
}: {
  pipe: SeonPipe | null
  error: string | null
  loading: boolean
}) {
  if (error) return <Notice tone="error">Состояние вебхуков не загрузилось: {error}</Notice>
  if (loading && !pipe) return <Skeleton className="h-12 w-full" />
  if (!pipe) return null

  return (
    <div>
      <p className="stencil-label text-fg-muted">
        Вебхуки SEON · сегодня, {pipe.day} UTC
      </p>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4 lg:grid-cols-7">
        <PipeItem
          label="Последний вебхук"
          value={pipe.last_webhook_at ? formatMoment(pipe.last_webhook_at) : "не было"}
          wide
        />
        <PipeItem label="Принято" value={formatInt(pipe.accepted)} tone="approve" />
        <PipeItem
          label="Отклонено по подписи"
          value={formatInt(pipe.rejected)}
          tone={pipe.rejected > 0 ? "decline" : undefined}
        />
        <PipeItem
          label="Без подписи"
          value={formatInt(pipe.unsigned)}
          tone={pipe.unsigned > 0 ? "review" : undefined}
          hint="приняты, но подпись не проверялась"
        />
        <PipeItem label="Дубли" value={formatInt(pipe.duplicates)} />
        <PipeItem
          label="Битые"
          value={formatInt(pipe.broken)}
          tone={pipe.broken > 0 ? "decline" : undefined}
        />
        <PipeItem label="Двойник за 24 ч" value={formatInt(pipe.twin_last_24h)} />
      </dl>
    </div>
  )
}

function PipeItem({
  label,
  value,
  tone,
  hint,
  wide = false,
}: {
  label: string
  value: string
  tone?: "approve" | "review" | "decline"
  hint?: string
  wide?: boolean
}) {
  return (
    <div className={cn("min-w-0", wide && "col-span-2 sm:col-span-1")} title={hint}>
      <dt className="truncate text-xs text-fg-subtle">{label}</dt>
      <dd
        className={cn(
          "mt-0.5  tabular-nums",
          wide ? "text-xs text-fg" : "text-base",
          tone ? TONE_TEXT[tone] : !wide && "text-fg",
        )}
      >
        {value}
      </dd>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* Свод                                                                      */
/* ------------------------------------------------------------------------- */

/** Покрытие за период (AMEND-59 шаг 3б): сколько решений, вебхуков и пар. */
function SummaryTiles({ summary, loading }: { summary: LiveSummary | null; loading: boolean }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Tile
          label="Пары"
          hint="есть и у нас, и у SEON"
          value={summary?.pairs}
          loading={loading}
          className="col-span-2 sm:col-span-1"
          strong
        />
        <Tile label="Наши решения" hint="строки двойника" value={summary?.ours} loading={loading} />
        <Tile label="Вебхуки SEON" hint="решения SEON" value={summary?.seon} loading={loading} />
        <Tile
          label="Только у нас"
          hint="SEON ещё не прислал"
          value={summary?.ours_only}
          loading={loading}
        />
        <Tile
          label="Только у SEON"
          hint="пары у нас не нашлось"
          value={summary?.seon_only}
          loading={loading}
        />
        <Tile
          label="Неоднозначные"
          hint="из «только у SEON»: две строки на равном расстоянии"
          value={summary?.ambiguous}
          loading={loading}
        />
      </div>
      {summary && !loading && <CoverageNote summary={summary} />}
    </div>
  )
}

const SOURCE_ORDER: LiveDecisionSource[] = ["score", "state", "review"]

/** Чем склеены пары и откуда решения SEON — одной строкой под плитками. */
function CoverageNote({ summary }: { summary: LiveSummary }) {
  const matches = Object.entries(summary.pairs_by_match).filter(([, count]) => count > 0)
  const sources = SOURCE_ORDER.filter((source) => summary.seon_decision_sources[source])
  return (
    <p className="text-xs leading-relaxed text-fg-subtle">
      {matches.length > 0 && (
        <>
          Склейка:{" "}
          {matches
            .map(([match, count]) => `${MATCH_LABEL[match] ?? match} — ${formatInt(count)}`)
            .join(" · ")}
          .{" "}
        </>
      )}
      {sources.length > 0 && (
        <>
          Решение SEON:{" "}
          {sources
            .map(
              (source) =>
                `${DECISION_SOURCE_LABEL[source]} — ${formatInt(summary.seon_decision_sources[source] ?? 0)}`,
            )
            .join(" · ")}
          .{" "}
        </>
      )}
      По баллу — пороги двойника: APPROVE ниже {formatScore(summary.thresholds.approve_below)},
      DECLINE от {formatScore(summary.thresholds.decline_at)}; ручная проверка в SEON перекрывает.
    </p>
  )
}

function Tile({
  label,
  hint,
  value,
  loading,
  className,
  strong = false,
}: {
  label: string
  hint: string
  value: number | undefined
  loading: boolean
  className?: string
  strong?: boolean
}) {
  return (
    <div
      className={cn(
        "rounded border px-3 py-2.5",
        strong ? "border-accent/35 bg-accent-soft" : "border-line bg-surface-sunken",
        className,
      )}
    >
      <p className="stencil-label text-fg-muted">{label}</p>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-14" />
      ) : (
        <p
          className={cn(
            "mt-1  text-2xl tabular-nums leading-none",
            strong ? "text-accent" : "text-fg",
          )}
        >
          {formatInt(value)}
        </p>
      )}
      <p className="mt-1 text-xs leading-snug text-fg-subtle">{hint}</p>
    </div>
  )
}

function Panel({
  title,
  note,
  className,
  children,
}: {
  title: string
  note?: React.ReactNode
  className?: string
  children: React.ReactNode
}) {
  return (
    <section
      className={cn("animate-panel-in rounded border border-line bg-surface shadow-panel", className)}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-2.5">
        <h3 className="section-title">{title}</h3>
        {note && <span className="text-xs text-fg-subtle">{note}</span>}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  )
}

function PanelError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="py-6 text-center">
      <p className="text-sm font-medium text-decline">{message}</p>
      <Button className="mt-3" size="sm" onClick={onRetry}>
        Повторить
      </Button>
    </div>
  )
}

function Dot({ state }: { state: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
        state === "approve" && "bg-approve",
        state === "review" && "bg-review",
        state === "decline" && "bg-decline",
      )}
    />
  )
}

/**
 * Решётка решений: строки — SEON, столбцы — мы, как в `compare.DecisionMatrix`.
 * Пар нет — решётки нет: девять нулей читались бы как «ни разу не разошлись».
 */
function MatrixPanel({ summary, loading }: { summary: LiveSummary | null; loading: boolean }) {
  const matrix = summary?.matrix
  const share = matrix?.share_agreed == null ? null : Number(matrix.share_agreed) * 100

  return (
    <Panel
      title="Решения: SEON × мы"
      note={matrix && matrix.rows > 0 ? `совпало ${formatPercent(share)}` : undefined}
    >
      {loading || !matrix ? (
        <Skeleton className="h-32 w-full" />
      ) : matrix.rows === 0 ? (
        <p className="py-6 text-center text-xs text-fg-muted">
          За период нет пар с решениями у обеих сторон — сравнивать решения не на чем.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[300px] border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="border-b border-line px-2 py-1.5 text-left text-xs font-normal text-fg-subtle">
                    SEON ↓ · мы →
                  </th>
                  {DECISIONS.map((our) => (
                    <th
                      key={our}
                      scope="col"
                      className="stencil-label border-b border-line px-2 py-1.5 text-right"
                    >
                      <span className="inline-flex items-center gap-1">
                        <Dot state={our} />
                        {our.toUpperCase()}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DECISIONS.map((seon) => (
                  <tr key={seon}>
                    <th
                      scope="row"
                      className="stencil-label border-b border-line px-2 py-2 text-left"
                    >
                      <span className="inline-flex items-center gap-1">
                        <Dot state={seon} />
                        {seon.toUpperCase()}
                      </span>
                    </th>
                    {DECISIONS.map((our) => {
                      const count = matrix.cells[`seon_${seon}`]?.[`our_${our}`] ?? 0
                      const diagonal = seon === our
                      return (
                        <td
                          key={our}
                          className={cn(
                            "border-b border-line px-2 py-2 text-right  text-base tabular-nums",
                            diagonal && "bg-surface-sunken",
                            count === 0
                              ? "text-fg-subtle"
                              : diagonal
                                ? "text-fg"
                                : "font-medium text-decline",
                          )}
                        >
                          {formatInt(count)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-fg-subtle">
            Совпало {formatInt(matrix.agreed)} из {formatInt(matrix.rows)} пар. Серая диагональ —
            согласие, красное вне её — расхождение.
            {matrix.undecided > 0 &&
              ` Ещё ${formatInt(matrix.undecided)} пар не легли в решётку: у одной из сторон решение не из трёх.`}
          </p>
        </>
      )}
    </Panel>
  )
}

function DeltaPanel({ summary, loading }: { summary: LiveSummary | null; loading: boolean }) {
  const delta = summary?.delta
  return (
    <Panel
      title="Δ балла: мы − SEON"
      note={delta ? `по ${formatInt(delta.rows)} парам` : undefined}
    >
      {loading || !delta ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3">
            <DeltaItem
              label="Средняя Δ"
              value={formatDelta(delta.mean)}
              hint="плюс — мы строже SEON"
            />
            <DeltaItem label="Средняя |Δ|" value={formatAbs(delta.mean_abs)} />
            <DeltaItem label="Медиана |Δ|" value={formatAbs(delta.median_abs)} />
            <DeltaItem label="p90 |Δ|" value={formatAbs(delta.p90_abs)} hint="у 9 из 10 пар не больше" />
          </dl>
          {delta.rows === 0 && (
            <p className="mt-2 text-xs text-fg-subtle">
              Пар с баллом у обеих сторон за период нет — разницу считать не из чего.
            </p>
          )}
        </>
      )}
    </Panel>
  )
}

function DeltaItem({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded border border-line bg-surface-sunken px-3 py-2">
      <dt className="stencil-label text-fg-muted">{label}</dt>
      <dd className="mt-1 text-xl tabular-nums leading-none text-fg">{value}</dd>
      {hint && <p className="mt-1 text-xs leading-snug text-fg-subtle">{hint}</p>}
    </div>
  )
}

function TopRulesPanel({ summary, loading }: { summary: LiveSummary | null; loading: boolean }) {
  return (
    <Panel
      title="Правила врозь"
      note={summary ? `пар со сверкой правил: ${formatInt(summary.rules_compared)}` : undefined}
      className="lg:col-span-2"
    >
      {loading || !summary ? (
        <Skeleton className="h-28 w-full" />
      ) : summary.rules_compared === 0 ? (
        <p className="py-4 text-center text-xs text-fg-muted">
          За период нет пар, где списки правил есть у обеих сторон.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <RuleTop
            title="Чаще всего только у нас"
            hint="сработали у двойника, у SEON — нет"
            items={summary.top_extra}
            total={summary.rules_compared}
            tone="ours"
          />
          <RuleTop
            title="Чаще всего только у SEON"
            hint="у нас не сработали или не проверялись: пропуски по данным двойник не пишет"
            items={summary.top_missed}
            total={summary.rules_compared}
            tone="seon"
          />
        </div>
      )}
    </Panel>
  )
}

function RuleTop({
  title,
  hint,
  items,
  total,
  tone,
}: {
  title: string
  hint: string
  items: LiveRuleCount[]
  total: number
  tone: "ours" | "seon"
}) {
  return (
    <div className="min-w-0">
      <p className="stencil-label text-fg">{title}</p>
      <p className="mt-0.5 text-xs leading-snug text-fg-subtle">{hint}</p>
      {items.length === 0 ? (
        <p className="mt-2 text-xs text-fg-muted">Таких правил нет.</p>
      ) : (
        <ol className="mt-2 space-y-1.5">
          {items.map((rule) => (
            <li key={rule.seon_id} className="min-w-0">
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="flex min-w-0 items-baseline gap-1.5">
                  <span className="shrink-0 text-fg">{rule.seon_id}</span>
                  {rule.blacklist ? (
                    <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.04em] text-decline">
                      чс
                    </span>
                  ) : (
                    <span className="truncate text-fg-muted" title={rule.name ?? undefined}>
                      {rule.name ?? "нет в каталоге"}
                    </span>
                  )}
                </span>
                <span className="shrink-0 tabular-nums text-fg-muted">
                  {formatInt(rule.count)}
                  <span className="text-fg-subtle"> / {formatInt(total)}</span>
                </span>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-surface-sunken">
                <div
                  className={cn("h-full rounded-full", tone === "ours" ? "bg-accent" : "bg-review")}
                  style={{ width: `${Math.min(100, (rule.count / Math.max(total, 1)) * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* Лента пар                                                                 */
/* ------------------------------------------------------------------------- */

function FeedPanel({
  page,
  error,
  loading,
  blocked,
  mismatch,
  unpaired,
  deltaText,
  deltaInvalid,
  open,
  offset,
  onMismatch,
  onUnpaired,
  onDelta,
  onReset,
  onToggle,
  onOffset,
  onRetry,
}: {
  page: LivePairsPage | null
  error: string | null
  loading: boolean
  blocked: boolean
  mismatch: boolean
  unpaired: boolean
  deltaText: string
  deltaInvalid: boolean
  open: string | null
  offset: number
  onMismatch: (value: boolean) => void
  onUnpaired: (value: boolean) => void
  onDelta: (value: string) => void
  onReset: () => void
  onToggle: (key: string) => void
  onOffset: (value: number) => void
  onRetry: () => void
}) {
  const filtered = mismatch || unpaired || deltaText.trim() !== ""
  const items = blocked ? [] : (page?.items ?? [])
  const total = blocked ? 0 : (page?.total ?? 0)
  const first = total === 0 ? 0 : offset + 1
  const last = Math.min(offset + items.length, total)

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line px-4 py-3">
        <h3 className="section-title mr-auto">Лента пар</h3>
        <label className="flex items-center gap-2 text-xs text-fg">
          <Checkbox checked={mismatch} onCheckedChange={(value) => onMismatch(value === true)} />
          Решения расходятся
        </label>
        <label className="flex items-center gap-2 text-xs text-fg">
          <Checkbox checked={unpaired} onCheckedChange={(value) => onUnpaired(value === true)} />
          Без пары
        </label>
        <label className="flex items-center gap-2 text-xs text-fg">
          |Δ| ≥
          <Input
            inputMode="decimal"
            value={deltaText}
            onChange={(event) => onDelta(event.target.value)}
            placeholder="0"
            aria-invalid={deltaInvalid}
            className={cn("h-7 w-16", deltaInvalid && "border-decline")}
          />
        </label>
        {filtered && (
          <Button size="sm" variant="ghost" onClick={onReset}>
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
            Сбросить
          </Button>
        )}
      </header>
      {deltaInvalid && (
        <div className="border-b border-line px-4 py-2">
          <Notice tone="warn">Порог |Δ| — число от 0 до 1000; пока поле не исправлено, действует прежний.</Notice>
        </div>
      )}

      {blocked ? (
        <p className="px-6 py-10 text-center text-xs text-fg-muted">
          Лента появится, когда период будет задан верно.
        </p>
      ) : error ? (
        <PanelError message={error} onRetry={onRetry} />
      ) : loading && !page ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-9 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <p className="text-sm font-medium text-fg">
            {filtered ? "С этими фильтрами пар нет." : "За период нет ни решений двойника, ни вебхуков SEON."}
          </p>
          <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
            {filtered
              ? "Снимите фильтр или расширьте период."
              : "Как только двойник примет решение или SEON пришлёт вебхук, строка появится здесь."}
          </p>
        </div>
      ) : (
        <div className={cn("transition-opacity", loading && "opacity-60")} aria-busy={loading}>
          <div className="hidden xl:block">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-40">Время</TableHead>
                  <TableHead>Транзакция</TableHead>
                  <TableHead className="w-44">Мы</TableHead>
                  <TableHead className="w-48">SEON</TableHead>
                  <TableHead align="right" className="w-20">
                    Δ
                  </TableHead>
                  <TableHead align="right" className="w-24">
                    Правил врозь
                  </TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((pair) => (
                  <PairRow
                    key={pair.key}
                    pair={pair}
                    open={open === pair.key}
                    onToggle={() => onToggle(pair.key)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="divide-y divide-line xl:hidden">
            {items.map((pair) => (
              <PairCard
                key={pair.key}
                pair={pair}
                open={open === pair.key}
                onToggle={() => onToggle(pair.key)}
              />
            ))}
          </ul>
        </div>
      )}

      {!error && !blocked && (
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
          <span className="text-xs text-fg-muted">
            {total === 0
              ? "0 из 0"
              : `${formatInt(first)}–${formatInt(last)} из ${formatInt(total)}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={offset === 0 || loading}
              onClick={() => onOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
              Назад
            </Button>
            <Button
              size="sm"
              disabled={!page?.has_more || loading}
              onClick={() => onOffset(offset + PAGE_SIZE)}
            >
              Дальше
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Button>
          </div>
        </footer>
      )}
    </section>
  )
}

/** Наша сторона пары: балл и решение, а без строки двойника — «нет у нас». */
function OurSide({ pair }: { pair: LivePair }) {
  if (!pair.ours) return <span className="text-xs italic text-fg-subtle">нет у нас</span>
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={cn(
          "text-sm tabular-nums",
          TONE_TEXT[decisionTone(pair.ours.state)],
        )}
      >
        {formatScore(pair.ours.fraud_score)}
      </span>
      <DecisionBadge state={pair.ours.state} />
    </span>
  )
}

/** Сторона SEON: балл, решение и, если была, ручная проверка в SEON. */
function SeonSide({ pair }: { pair: LivePair }) {
  if (!pair.seon) return <span className="text-xs italic text-fg-subtle">SEON ещё не прислал</span>
  const review = pair.review
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-2">
        <span
          className={cn(
            "text-sm tabular-nums",
            TONE_TEXT[decisionTone(pair.seon.decision)],
          )}
        >
          {formatScore(pair.seon.fraud_score)}
        </span>
        <SeonDecision seon={pair.seon} />
      </span>
      {review && (
        <span className="inline-flex flex-wrap items-center gap-1 text-xs text-fg-subtle">
          ручная: {review.state ?? EMPTY}
          {review.label && <Badge tone="accent">{review.label}</Badge>}
        </span>
      )}
    </span>
  )
}

function DeltaValue({ pair }: { pair: LivePair }) {
  return (
    <span
      className={cn(
        "tabular-nums",
        pair.delta === null ? "text-fg-subtle" : "text-fg",
        pair.decision_mismatch && "font-semibold text-decline",
      )}
    >
      {formatDelta(pair.delta)}
    </span>
  )
}

function PairRow({
  pair,
  open,
  onToggle,
}: {
  pair: LivePair
  open: boolean
  onToggle: () => void
}) {
  return (
    <>
      <TableRow
        onClick={onToggle}
        className={cn("cursor-pointer", open && "bg-surface-sunken")}
        data-state={open ? "open" : undefined}
      >
        <TableCell className="whitespace-nowrap text-xs text-fg-muted">
          {formatMoment(pair.at)}
        </TableCell>
        <TableCell className="max-w-0">
          <span className="flex items-center gap-1.5">
            {pair.decision_mismatch && (
              <span aria-label="решения расходятся" className="h-1.5 w-1.5 shrink-0 rounded-full bg-decline" />
            )}
            <span className="truncate text-xs text-fg" title={pair.transaction_id ?? undefined}>
              {pair.transaction_id ?? EMPTY}
            </span>
          </span>
        </TableCell>
        <TableCell>
          <OurSide pair={pair} />
        </TableCell>
        <TableCell>
          <SeonSide pair={pair} />
        </TableCell>
        <TableCell className="text-right">
          <DeltaValue pair={pair} />
        </TableCell>
        <TableNumeric className="text-fg-muted">
          {pair.rules_differ === null ? EMPTY : formatInt(pair.rules_differ)}
        </TableNumeric>
        <TableCell className="text-right">
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? "Свернуть разбор" : "Разбор пары"}
            onClick={(event) => {
              event.stopPropagation()
              onToggle()
            }}
            className="text-fg-subtle transition-colors hover:text-accent"
          >
            <ChevronDown
              className={cn("h-4 w-4 transition-transform duration-150", open && "rotate-180")}
              strokeWidth={2}
            />
          </button>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={7} className="bg-surface-sunken/60 px-4 py-3">
            <PairDetails pair={pair} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function PairCard({
  pair,
  open,
  onToggle,
}: {
  pair: LivePair
  open: boolean
  onToggle: () => void
}) {
  return (
    <li className={cn(open && "bg-surface-sunken")}>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="block w-full px-4 py-3 text-left"
      >
        <span className="flex items-center justify-between gap-3">
          <span className="text-xs text-fg-muted">{formatMoment(pair.at)}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-fg-subtle transition-transform duration-150",
              open && "rotate-180",
            )}
            strokeWidth={2}
          />
        </span>
        <span className="mt-1 flex items-center gap-1.5">
          {pair.decision_mismatch && (
            <span aria-label="решения расходятся" className="h-1.5 w-1.5 shrink-0 rounded-full bg-decline" />
          )}
          <span className="truncate text-xs text-fg">{pair.transaction_id ?? EMPTY}</span>
        </span>
        <span className="mt-2 grid grid-cols-2 gap-3">
          <span className="min-w-0">
            <span className="stencil-label block text-fg-subtle">Мы</span>
            <OurSide pair={pair} />
          </span>
          <span className="min-w-0">
            <span className="stencil-label block text-fg-subtle">SEON</span>
            <SeonSide pair={pair} />
          </span>
        </span>
        <span className="mt-2 flex items-center gap-4 text-xs text-fg-subtle">
          <span>
            Δ <DeltaValue pair={pair} />
          </span>
          <span>
            правил врозь{" "}
            <span className="text-fg-muted">
              {pair.rules_differ === null ? EMPTY : formatInt(pair.rules_differ)}
            </span>
          </span>
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <PairDetails pair={pair} />
        </div>
      )}
    </li>
  )
}

/** Разбор пары: правила, ручная проверка в SEON и путь в карточку. */
function PairDetails({ pair }: { pair: LivePair }) {
  return (
    <div className="space-y-3">
      {pair.missing === "seon" && (
        <p className="text-xs text-fg-muted">
          SEON ещё не прислал вебхук по этой транзакции — сравнивать правила не с чем.
        </p>
      )}
      {pair.missing === "ours" && (
        <p className="text-xs text-fg-muted">
          Строки двойника по этой транзакции нет у нас: решение пришло только от SEON.
        </p>
      )}
      <RuleDiffView rules={pair.rules} />
      {pair.seon && (
        <div>
          <p className="stencil-label mb-1.5 text-fg-subtle">Ручная проверка в SEON</p>
          <ReviewView review={pair.review} />
          <p className="mt-1.5 text-xs text-fg-subtle">
            вебхук v{pair.seon.version} · {formatMoment(pair.seon.received_at)} ·{" "}
            {pair.seon.signature_verified ? "подписан" : "без подписи"} · склейка{" "}
            {matchLabel(pair.match ?? pair.seon.twin_match)}
          </p>
        </div>
      )}
      {pair.ours ? (
        <Link
          to={`/transactions/${pair.ours.id}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          Карточка транзакции
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
        </Link>
      ) : (
        <p className="text-xs text-fg-subtle">Карточки нет: у нас нет строки этой транзакции.</p>
      )}
    </div>
  )
}
